'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import tooltips from '../constants/tooltips'
import { useAuth } from '../contexts/AuthContext'
import { stripLocalePrefix } from '../app/i18n/supported-locales'

// Public, SEO-facing routes render their (server-rendered) content immediately — no auth
// spinner. These pages are identical for anonymous and authenticated users, so blocking them
// on the client-side Firebase auth check only hurt LCP and hid content from crawlers.
//
// Auth-gated app routes (generate, chat, settings, profile, gems, …) KEEP the spinner: it
// prevents a flash of logged-out UI and premature redirects while auth resolves.
const PUBLIC_EXACT = new Set([
  '/',
  '/ai-generator',
  '/nsfw-ai',
  '/nsfw-ai-chat',
  '/ai-hentai',
  '/ai-porn',
  '/gay-ai-porn',
  '/ai-porn-video-generator',
  '/contact',
  '/privacypolicy',
  '/termsofservice',
  '/refund',
  '/dmcapolicy',
  '/contentremoval',
  '/contentmoderationpolicy',
  '/docs',
  '/character-ai-down',
  // /upgrade is the pricing page. BOTH paths must be listed, because a middleware rewrite makes
  // usePathname() disagree between server and client: for a logged-out visitor the middleware
  // rewrites /upgrade → /upgrade-public, and SSR sees the INTERNAL path while the hydrating
  // client sees the BROWSER path. Listing only one would make the gate flip on at hydration and
  // replace the server-rendered pricing page with a spinner.
  //
  // Dropping the gate for the signed-in /upgrade screen is safe: UpgradePage already guards
  // itself (`if (discountLoading || !maxAbValue) return <ClipLoader/>`), so it cannot render
  // half-loaded pricing — and now the header/footer paint immediately instead of behind a
  // full-screen spinner.
  '/upgrade',
  '/upgrade-public',
])

// Localized routes are `/{locale}/…`; strip a leading supported-locale segment before matching
// so e.g. `/de/nsfw-ai` is recognized as public (otherwise the auth spinner blocks the locale
// pages during SSR and ships no crawlable content).
function isPublicRoute(pathname) {
  if (!pathname) return false
  const p = stripLocalePrefix(pathname)
  if (
    p === '/landing-preview' ||
    p === '/landing-v1/chat' ||
    p === '/landing-v1/create' ||
    p === '/landing-v4/create' ||
    p === '/landing-v5/current' ||
    p === '/landing-v5/revised'
  )
    return true
  if (PUBLIC_EXACT.has(p)) return true
  if (p.startsWith('/articles')) return true
  return false
}

function LoadingIndicator({ children, initialTooltip }) {
  const { loading, user, userDataLoaded, planHint } = useAuth()
  const pathname = usePathname()
  const [randomTooltip, setRandomTooltip] = useState(initialTooltip)

  useEffect(() => {
    // Pick a new tooltip for the next time the loading indicator shows
    if (!loading) {
      setRandomTooltip(tooltips[Math.floor(Math.random() * tooltips.length)])
    }
  }, [loading, tooltips])

  const spinnerStyle = {
    width: '35px',
    height: '35px',
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderTop: '4px solid #FFF',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  }

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column', // Changed to column for vertical stacking
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'var(--background-bg-color)',
  }

  const keyframesStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `

  // Only block auth-gated routes. usePathname() returns the same value on the server and on
  // the first client render, so this decision is hydration-stable (no mismatch).
  //
  // Second clause = the fresh sign-in case: `loading` flips false the instant Firebase confirms
  // the user, but their gems/plan/avatar arrive a beat later via loadSubscriptionData. A FIRST-
  // TIME sign-in has no cached hints (`planHint` is null until the first pc_plan cookie is set),
  // so without this the authed shell would render half-empty and pop in. Keep the full-screen
  // loader until their data lands. Returning users (planHint present) keep their optimistic
  // instant-render — this only engages when there's nothing cached to render yet.
  const blocking =
    !isPublicRoute(pathname) &&
    (loading || (user && !userDataLoaded && !planHint))

  return (
    <>
      {blocking ? (
        <div style={containerStyle}>
          <style>{keyframesStyle}</style>
          <div style={spinnerStyle}></div>

          <img
            src="/assets/promptchan_logo.svg"
            alt="Promptchan"
            // Intrinsic size of the SVG, so the splash reserves the box before load.
            width="495"
            height="91"
            className="md:h-6 h-4 w-auto my-8"
          />
          <p className="text-[#7a74eb] p-2 px-5 my-6 font-semibold tracking-tight md:text-base text-sm text-center rounded-full md:w-auto w-2/3">
            {randomTooltip}
          </p>
        </div>
      ) : (
        children
      )}
    </>
  )
}

export default LoadingIndicator
